import { useEffect, useMemo, useRef, useState } from "react";
import type { Vehicle } from "./useVehicleStream";
import type { HistoryStore, Sample, VehicleHistory } from "./positionHistory";
import {
  buildRouteProjection,
  type ProjectedStop,
  type RawRouteGeometry,
  type RouteProjection,
} from "./routeProjection";
import "./Bunching.css";

// Bunching detector tab.
//
// Walks every (route × direction) train cluster, sorts by along-route
// distance, and surfaces consecutive pairs whose implied headway has
// collapsed below a healthy threshold. The cruise speed used to convert
// "meters between trains" → "minutes between trains" matches the rest of
// the app (PlatformDisplay's CRUISE_M_PER_S) so the numbers feel
// consistent across tabs.

const CRUISE_M_PER_S = 13.4;
const SEVERE_SEC = 90;
const WARNING_SEC = 180;
const STALE_AFTER_SEC = 120;
const BAR_CAP_M = 2000;
const TREND_WINDOW_SEC = 30;
const TREND_LOOKBACK_SEC = 60;
const TREND_MATCH_TOLERANCE_SEC = 20;
// Significant change in gap (meters) required before we tag a pair as
// "closing" or "opening" rather than "steady". Below this is within GPS
// jitter / sample-quantization noise.
const TREND_SIGNIFICANT_M = 80;
const RESOLVED_HISTORY_CAP = 10;

type DirSpec = {
  key: string;
  label: string;
  /** Lowercased substrings against trip_headsign. */
  matches: string[];
  /** Lowercased substring used to figure out which end of the polyline this direction heads toward. */
  terminusHint: string;
};

type RouteSpec = {
  routeId: string;
  directions: DirSpec[];
};

const ROUTES: RouteSpec[] = [
  {
    routeId: "40_100479",
    directions: [
      {
        key: "N",
        label: "Northbound",
        matches: ["lynnwood"],
        terminusHint: "lynnwood",
      },
      {
        key: "S",
        label: "Southbound",
        matches: ["federal way", "angle lake"],
        terminusHint: "angle lake",
      },
    ],
  },
  {
    routeId: "40_2LINE",
    directions: [
      {
        key: "E",
        label: "Eastbound",
        matches: ["redmond"],
        terminusHint: "redmond",
      },
      {
        key: "W",
        label: "Westbound",
        matches: ["lynnwood"],
        terminusHint: "lynnwood",
      },
    ],
  },
];

type Severity = "severe" | "warning";
type Trend = "closing" | "opening" | "steady";

type TrainSlot = {
  vehicleId: string;
  distM: number;
  /** Position along direction of travel (distM * dirSign); larger = farther ahead. */
  progressM: number;
  routeId: string;
  routeShortName: string;
  routeColor: string;
  ts: number;
  headsign?: string;
};

type PairIncident = {
  kind: "pair";
  pairKey: string;
  routeId: string;
  routeShortName: string;
  routeColor: string;
  directionLabel: string;
  trailing: TrainSlot;
  leading: TrainSlot;
  gapM: number;
  gapSec: number;
  severity: Severity;
  trend: Trend | null;
  prevGapM: number | null;
  nearestStop: string | null;
};

type ConvoyIncident = {
  kind: "convoy";
  convoyKey: string;
  routeId: string;
  routeShortName: string;
  routeColor: string;
  directionLabel: string;
  trains: TrainSlot[];
  pairs: PairIncident[];
  /** Worst (smallest) gapSec across the convoy's pairs. */
  minGapSec: number;
  severity: Severity;
};

type Incident = PairIncident | ConvoyIncident;

type ResolvedRecord = {
  pairKey: string;
  routeShortName: string;
  routeColor: string;
  directionLabel: string;
  vehicleA: string;
  vehicleB: string;
  startedAt: number;
  resolvedAt: number;
  peakSeverity: Severity;
  peakGapSec: number;
  nearestStop: string | null;
};

type ActiveTrack = {
  pairKey: string;
  routeShortName: string;
  routeColor: string;
  directionLabel: string;
  vehicleA: string;
  vehicleB: string;
  startedAt: number;
  lastSeenAt: number;
  peakSeverity: Severity;
  peakGapSec: number;
  nearestStop: string | null;
};

type Props = {
  vehicles: Vehicle[];
  history: HistoryStore;
  historyVersion: number;
  connected: boolean;
  historical?: boolean;
};

export function Bunching({
  vehicles,
  history,
  historyVersion,
  connected,
  historical = false,
}: Props) {
  const [projections, setProjections] = useState<RouteProjection[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Re-tick once a second so the freshness readout stays current.
  // Frozen during historical scrubbing — the parent's historyVersion bumps
  // are enough to drive re-renders for the scrubbed instant.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    if (historical) return;
    const id = window.setInterval(() => setNowTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [historical]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/routes/geometry");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as RawRouteGeometry[];
        if (cancelled) return;
        const projs = data
          .map(buildRouteProjection)
          .filter((p) => p.lengthM > 0);
        setProjections(projs);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // nowSec = newest snapshot ts when scrubbing history; wall clock otherwise.
  const newestTs = useMemo(() => {
    let m = 0;
    for (const v of vehicles) if (v.ts && v.ts > m) m = v.ts;
    return m;
  }, [vehicles]);
  const nowSec = historical
    ? newestTs || Math.floor(Date.now() / 1000)
    : Math.floor(Date.now() / 1000);

  const projByRoute = useMemo(() => {
    const m = new Map<string, RouteProjection>();
    if (projections) for (const p of projections) m.set(p.routeId, p);
    return m;
  }, [projections]);

  // Direction-sign per (route, direction key): +1 if direction's terminus
  // sits at the high-distM end of the canonical projection, -1 otherwise.
  const dirSignByKey = useMemo(() => {
    const m = new Map<string, 1 | -1>();
    if (!projections) return m;
    for (const route of ROUTES) {
      const proj = projByRoute.get(route.routeId);
      if (!proj) continue;
      for (const d of route.directions) {
        m.set(`${route.routeId}|${d.key}`, inferDirSign(proj.stops, d.terminusHint));
      }
    }
    return m;
  }, [projections, projByRoute]);

  // Heart of the tab: derive incident list from the current vehicle set,
  // memoized on the SSE history version + the snapshot reference so it
  // recomputes once per tick rather than on every parent re-render.
  const { incidents, totalTrains, routesWithService } = useMemo(() => {
    if (!projections) {
      return { incidents: [] as Incident[], totalTrains: 0, routesWithService: 0 };
    }

    const out: Incident[] = [];
    let total = 0;
    const activeRoutes = new Set<string>();

    for (const route of ROUTES) {
      const proj = projByRoute.get(route.routeId);
      if (!proj) continue;

      for (const dir of route.directions) {
        const dirSign = dirSignByKey.get(`${route.routeId}|${dir.key}`) ?? 1;
        const trains: TrainSlot[] = [];

        for (const v of vehicles) {
          if (v.route_id !== route.routeId) continue;
          if (!matchesDirection(v.trip_headsign, dir)) continue;
          // In-service filter: must have a fresh sample. We deliberately
          // don't gate on v.status — Sound Transit's feed sometimes lacks it
          // for trains that are clearly carrying passengers.
          const ts = v.ts || nowSec;
          if (nowSec - ts > STALE_AFTER_SEC) continue;
          const distM = proj.project(v.lat, v.lon);
          if (distM == null) continue;
          trains.push({
            vehicleId: v.vehicle_id,
            distM,
            progressM: distM * dirSign,
            routeId: route.routeId,
            routeShortName: proj.shortName || route.routeId,
            routeColor: proj.color,
            ts,
            headsign: v.trip_headsign,
          });
        }

        if (trains.length === 0) continue;
        total += trains.length;
        activeRoutes.add(route.routeId);

        if (trains.length < 2) continue;

        // Sort along direction of travel so the leading train is last.
        trains.sort((a, b) => a.progressM - b.progressM);

        // Build the per-pair incidents for this (route, direction). We
        // stash all of them first so we can promote consecutive bunched
        // runs into a single "convoy" entry afterward.
        const dirPairs: PairIncident[] = [];
        for (let i = 0; i < trains.length - 1; i++) {
          const trailing = trains[i];
          const leading = trains[i + 1];
          const gapM = leading.progressM - trailing.progressM;
          const gapSec = gapM / CRUISE_M_PER_S;

          let severity: Severity | null = null;
          if (gapSec < SEVERE_SEC) severity = "severe";
          else if (gapSec < WARNING_SEC) severity = "warning";
          if (severity == null) {
            // Not bunching — but we still want it so convoy detection can
            // see the gap. We tag a sentinel and filter below.
            dirPairs.push({
              kind: "pair",
              pairKey: pairKeyFor(trailing.vehicleId, leading.vehicleId),
              routeId: route.routeId,
              routeShortName: proj.shortName || route.routeId,
              routeColor: proj.color,
              directionLabel: dir.label,
              trailing,
              leading,
              gapM,
              gapSec,
              severity: "warning", // placeholder, will be filtered out
              trend: null,
              prevGapM: null,
              nearestStop: null,
            });
            continue;
          }

          const nearestStop = nearestStopName(proj.stops, trailing.distM);
          const { trend, prevGapM } = computeTrend(
            history,
            proj,
            dirSign,
            trailing.vehicleId,
            leading.vehicleId,
            nowSec,
          );

          dirPairs.push({
            kind: "pair",
            pairKey: pairKeyFor(trailing.vehicleId, leading.vehicleId),
            routeId: route.routeId,
            routeShortName: proj.shortName || route.routeId,
            routeColor: proj.color,
            directionLabel: dir.label,
            trailing,
            leading,
            gapM,
            gapSec,
            severity,
            trend,
            prevGapM,
            nearestStop,
          });
        }

        // Convoy detection: walk runs of consecutive pairs that are all
        // bunching (severity is real, not the placeholder). A run of
        // length >= 2 pairs (3+ trains) becomes a convoy entry that
        // supersedes its constituent pairs. Shorter runs surface as
        // individual pair entries.
        const isBunching = (p: PairIncident) =>
          p.gapSec < WARNING_SEC; // both severe and warning thresholds

        let i = 0;
        while (i < dirPairs.length) {
          if (!isBunching(dirPairs[i])) {
            i++;
            continue;
          }
          let j = i;
          while (j + 1 < dirPairs.length && isBunching(dirPairs[j + 1])) j++;
          const runPairs = dirPairs.slice(i, j + 1);
          if (runPairs.length >= 2) {
            const convoyTrains: TrainSlot[] = [runPairs[0].trailing];
            for (const p of runPairs) convoyTrains.push(p.leading);
            const minGapSec = Math.min(...runPairs.map((p) => p.gapSec));
            const severity: Severity =
              minGapSec < SEVERE_SEC ? "severe" : "warning";
            out.push({
              kind: "convoy",
              convoyKey: `convoy:${route.routeId}:${dir.key}:${convoyTrains.map((t) => t.vehicleId).join("-")}`,
              routeId: route.routeId,
              routeShortName: proj.shortName || route.routeId,
              routeColor: proj.color,
              directionLabel: dir.label,
              trains: convoyTrains,
              pairs: runPairs,
              minGapSec,
              severity,
            });
          } else {
            // Single bunched pair; only emit if it actually crosses the
            // threshold (the placeholder branch above also reaches here
            // when gapSec < WARNING_SEC, but those entries always have a
            // real severity since we set it before pushing).
            out.push(runPairs[0]);
          }
          i = j + 1;
        }
      }
    }

    // Sort: severe before warning, then by smallest gap first.
    out.sort((a, b) => {
      const sa = severityRank(a.severity);
      const sb = severityRank(b.severity);
      if (sa !== sb) return sa - sb;
      return minGap(a) - minGap(b);
    });

    return {
      incidents: out,
      totalTrains: total,
      routesWithService: activeRoutes.size,
    };
    // historyVersion is the cadence trigger; vehicles is the data source.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyVersion, vehicles, projections, projByRoute, dirSignByKey, historical]);

  // Track active-and-resolved bunching incidents so the bottom panel can
  // show "this just cleared up". Skipped during historical scrubbing —
  // resolution is a live concept and would otherwise thrash the list as
  // the user drags the time slider.
  const activeRef = useRef<Map<string, ActiveTrack>>(new Map());
  const [resolved, setResolved] = useState<ResolvedRecord[]>([]);
  useEffect(() => {
    if (historical) return;
    const active = activeRef.current;
    const seen = new Set<string>();
    for (const inc of incidents) {
      const pairList: PairIncident[] =
        inc.kind === "convoy" ? inc.pairs : [inc];
      for (const p of pairList) {
        seen.add(p.pairKey);
        const existing = active.get(p.pairKey);
        const peakSeverity: Severity =
          existing && severityRank(existing.peakSeverity) <= severityRank(p.severity)
            ? existing.peakSeverity
            : p.severity;
        const peakGapSec =
          existing && existing.peakGapSec < p.gapSec
            ? existing.peakGapSec
            : p.gapSec;
        active.set(p.pairKey, {
          pairKey: p.pairKey,
          routeShortName: p.routeShortName,
          routeColor: p.routeColor,
          directionLabel: p.directionLabel,
          vehicleA: p.trailing.vehicleId,
          vehicleB: p.leading.vehicleId,
          startedAt: existing?.startedAt ?? nowSec,
          lastSeenAt: nowSec,
          peakSeverity,
          peakGapSec,
          nearestStop: p.nearestStop ?? existing?.nearestStop ?? null,
        });
      }
    }
    const newlyResolved: ResolvedRecord[] = [];
    for (const [key, track] of active) {
      if (seen.has(key)) continue;
      newlyResolved.push({
        pairKey: track.pairKey,
        routeShortName: track.routeShortName,
        routeColor: track.routeColor,
        directionLabel: track.directionLabel,
        vehicleA: track.vehicleA,
        vehicleB: track.vehicleB,
        startedAt: track.startedAt,
        resolvedAt: nowSec,
        peakSeverity: track.peakSeverity,
        peakGapSec: track.peakGapSec,
        nearestStop: track.nearestStop,
      });
      active.delete(key);
    }
    if (newlyResolved.length > 0) {
      setResolved((prev) =>
        [...newlyResolved, ...prev].slice(0, RESOLVED_HISTORY_CAP),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyVersion, incidents, historical]);

  const [showHistory, setShowHistory] = useState(false);

  if (error) {
    return (
      <div className="bunching-empty">Couldn't load route geometry: {error}</div>
    );
  }
  if (!projections) {
    return <div className="bunching-empty">Loading route geometry…</div>;
  }

  const severeCount = incidents.filter(
    (i) => i.severity === "severe",
  ).length;
  const warningCount = incidents.filter(
    (i) => i.severity === "warning",
  ).length;
  const heroTone: "ok" | "warn" | "alert" =
    severeCount > 0 ? "alert" : warningCount > 0 ? "warn" : "ok";
  const heroIcon = heroTone === "alert" ? "🚨" : heroTone === "warn" ? "⚠️" : "✅";
  const heroText =
    heroTone === "alert"
      ? `${severeCount} bunching event${severeCount === 1 ? "" : "s"} detected`
      : heroTone === "warn"
        ? `${warningCount} pair${warningCount === 1 ? "" : "s"} running close`
        : "Spacing looks normal";

  const freshnessSec = newestTs > 0 ? Math.max(0, nowSec - newestTs) : null;

  return (
    <div className="bunching">
      <header className={`bunching-hero bunching-hero-${heroTone}`}>
        <div className="bunching-hero-stripe" aria-hidden />
        <div className="bunching-hero-main">
          <div className={`bunching-pill bunching-pill-${heroTone}`}>
            <span className="bunching-pill-icon" aria-hidden>
              {heroIcon}
            </span>
            <span className="bunching-pill-text">{heroText}</span>
          </div>
          <div className="bunching-hero-sub">
            {totalTrains} train{totalTrains === 1 ? "" : "s"} in service across{" "}
            {routesWithService} route{routesWithService === 1 ? "" : "s"}.
          </div>
        </div>
        <div className="bunching-hero-aside">
          <div
            className={`bunching-freshness ${historical ? "history" : connected ? "ok" : "wait"}`}
          >
            {historical
              ? "◐ history"
              : connected
                ? "● live"
                : "○ waiting…"}
          </div>
          <div className="bunching-freshness-age">
            {freshnessSec == null
              ? "no data yet"
              : `last update ${freshnessSec}s ago`}
          </div>
        </div>
      </header>

      {incidents.length === 0 ? (
        <div className="bunching-empty-card">
          <div className="bunching-empty-check" aria-hidden>
            ✅
          </div>
          <div className="bunching-empty-title">No bunching detected</div>
          <div className="bunching-empty-body">
            Every train is spaced at least {Math.round(WARNING_SEC / 60)}:00
            apart. Right on schedule.
          </div>
          <div className="bunching-empty-hint">
            Bookmark this tab, check back during rush hour 🚆
          </div>
        </div>
      ) : (
        <ul className="bunching-list">
          {incidents.map((inc) =>
            inc.kind === "convoy" ? (
              <li key={inc.convoyKey} className="bunching-card-wrap">
                <ConvoyCard incident={inc} />
              </li>
            ) : (
              <li key={inc.pairKey} className="bunching-card-wrap">
                <PairCard incident={inc} />
              </li>
            ),
          )}
        </ul>
      )}

      <section className="bunching-resolved">
        <button
          type="button"
          className="bunching-resolved-toggle"
          onClick={() => setShowHistory((v) => !v)}
          aria-expanded={showHistory}
        >
          <span aria-hidden>{showHistory ? "▾" : "▸"}</span> Recently resolved (
          {resolved.length})
        </button>
        {showHistory && (
          <div className="bunching-resolved-body">
            {resolved.length === 0 ? (
              <div className="bunching-resolved-empty">
                Nothing has resolved this session.
              </div>
            ) : (
              <ul className="bunching-resolved-list">
                {resolved.map((r) => (
                  <li key={`${r.pairKey}:${r.resolvedAt}`} className="bunching-resolved-row">
                    <span
                      className="bunching-resolved-bullet"
                      style={{ background: r.routeColor }}
                      aria-hidden
                    />
                    <span className="bunching-resolved-label">
                      <strong>
                        Train {shortVehicleId(r.vehicleA)} + Train{" "}
                        {shortVehicleId(r.vehicleB)}
                      </strong>{" "}
                      <span className="bunching-resolved-meta">
                        {r.routeShortName} · {r.directionLabel}
                        {r.nearestStop ? ` · near ${r.nearestStop}` : ""}
                      </span>
                    </span>
                    <span
                      className={`bunching-resolved-peak bunching-peak-${r.peakSeverity}`}
                    >
                      peak {r.peakSeverity === "severe" ? "🚨" : "⚠️"}{" "}
                      {formatGap(r.peakGapSec)}
                    </span>
                    <span className="bunching-resolved-time">
                      {formatRange(r.startedAt, r.resolvedAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function PairCard({ incident }: { incident: PairIncident }) {
  return (
    <article
      className={`bunching-card bunching-card-${incident.severity}`}
      style={{ "--route-color": incident.routeColor } as React.CSSProperties}
    >
      <div className="bunching-stripe" aria-hidden />
      <div className="bunching-card-body">
        <div className="bunching-card-left">
          <div className="bunching-badges">
            <span
              className={`bunching-sev bunching-sev-${incident.severity}`}
            >
              {incident.severity === "severe" ? "🚨 BUNCHING" : "⚠️ CLOSE"}
            </span>
            {incident.trend && (
              <span className={`bunching-trend bunching-trend-${incident.trend}`}>
                {trendGlyph(incident.trend)} {incident.trend}
              </span>
            )}
          </div>
          <div className="bunching-trains">
            <span
              className="bunching-bullet"
              style={{ background: incident.routeColor }}
              aria-hidden
            />
            <span className="bunching-train-id">
              Train {shortVehicleId(incident.trailing.vehicleId)}
            </span>
            <span className="bunching-plus" aria-hidden>
              +
            </span>
            <span
              className="bunching-bullet"
              style={{ background: incident.routeColor }}
              aria-hidden
            />
            <span className="bunching-train-id">
              Train {shortVehicleId(incident.leading.vehicleId)}
            </span>
          </div>
          <div className="bunching-where">
            {incident.routeShortName} · {incident.directionLabel}
            {incident.nearestStop ? ` · near ${incident.nearestStop}` : ""}
          </div>
          <SpacingBar
            color={incident.routeColor}
            gapM={incident.gapM}
            severity={incident.severity}
          />
        </div>
        <div className={`bunching-gap bunching-gap-${incident.severity}`}>
          <div className="bunching-gap-value">{formatGap(incident.gapSec)}</div>
          <div className="bunching-gap-label">
            {Math.round(incident.gapM)} m apart
          </div>
        </div>
      </div>
    </article>
  );
}

function ConvoyCard({ incident }: { incident: ConvoyIncident }) {
  return (
    <article
      className={`bunching-card bunching-card-convoy bunching-card-${incident.severity}`}
      style={{ "--route-color": incident.routeColor } as React.CSSProperties}
    >
      <div className="bunching-stripe" aria-hidden />
      <div className="bunching-card-body">
        <div className="bunching-card-left">
          <div className="bunching-badges">
            <span className={`bunching-sev bunching-sev-${incident.severity}`}>
              🚆 CONVOY
            </span>
          </div>
          <div className="bunching-convoy-title">
            🚆🚆🚆 CONVOY of {incident.trains.length} trains ·{" "}
            {incident.routeShortName} {incident.directionLabel}
          </div>
          <div className="bunching-where">
            Trains{" "}
            {incident.trains
              .map((t) => shortVehicleId(t.vehicleId))
              .join(" → ")}
          </div>
          <ul className="bunching-convoy-list">
            {incident.pairs.map((p) => (
              <li key={p.pairKey} className="bunching-convoy-row">
                <span
                  className={`bunching-sev-mini bunching-sev-${p.severity}`}
                >
                  {p.severity === "severe" ? "🚨" : "⚠️"}
                </span>
                <span className="bunching-convoy-row-trains">
                  {shortVehicleId(p.trailing.vehicleId)} +{" "}
                  {shortVehicleId(p.leading.vehicleId)}
                </span>
                <span className="bunching-convoy-row-where">
                  {p.nearestStop ? `near ${p.nearestStop}` : "—"}
                </span>
                <span
                  className={`bunching-convoy-row-gap bunching-gap-${p.severity}`}
                >
                  {formatGap(p.gapSec)}
                </span>
                {p.trend && (
                  <span className={`bunching-trend bunching-trend-${p.trend}`}>
                    {trendGlyph(p.trend)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
        <div className={`bunching-gap bunching-gap-${incident.severity}`}>
          <div className="bunching-gap-value">{formatGap(incident.minGapSec)}</div>
          <div className="bunching-gap-label">tightest in convoy</div>
        </div>
      </div>
    </article>
  );
}

function SpacingBar({
  color,
  gapM,
  severity,
}: {
  color: string;
  gapM: number;
  severity: Severity;
}) {
  // Trailing dot pinned at 0%, leading dot at gap/cap. We only render the
  // bar for individual pairs (not convoys) so the visual stays uncluttered.
  const frac = Math.min(1, Math.max(0, gapM / BAR_CAP_M));
  const leadLeft = `${frac * 100}%`;
  return (
    <div className="bunching-bar" aria-hidden>
      <div className="bunching-bar-track" />
      <div
        className={`bunching-bar-segment bunching-bar-segment-${severity}`}
        style={{ width: leadLeft }}
      />
      <div
        className="bunching-bar-dot bunching-bar-dot-trailing"
        style={{ left: 0, background: color }}
      />
      <div
        className="bunching-bar-dot bunching-bar-dot-leading"
        style={{ left: leadLeft, background: color }}
      />
      <div className="bunching-bar-scale">
        0 — {(BAR_CAP_M / 1000).toFixed(1)} km
      </div>
    </div>
  );
}

function inferDirSign(stops: ProjectedStop[], terminusHint: string): 1 | -1 {
  if (stops.length < 2) return 1;
  const t = terminusHint.toLowerCase();
  const head = stops.slice(0, 3).some((s) => s.name.toLowerCase().includes(t));
  const tail = stops.slice(-3).some((s) => s.name.toLowerCase().includes(t));
  if (tail && !head) return 1;
  if (head && !tail) return -1;
  return 1;
}

function matchesDirection(
  headsign: string | undefined,
  dir: DirSpec,
): boolean {
  if (!headsign) return false;
  const h = headsign.toLowerCase();
  return dir.matches.some((m) => h.includes(m));
}

function nearestStopName(
  stops: ProjectedStop[],
  distM: number,
): string | null {
  let best: ProjectedStop | null = null;
  let bestDiff = Infinity;
  for (const s of stops) {
    const d = Math.abs(s.distM - distM);
    if (d < bestDiff) {
      bestDiff = d;
      best = s;
    }
  }
  return best ? best.name : null;
}

function sampleNear(
  hist: VehicleHistory | undefined,
  ts: number,
  toleranceSec: number,
): Sample | undefined {
  if (!hist || hist.samples.length === 0) return undefined;
  let best: Sample | undefined;
  let bestDiff = Infinity;
  // Linear scan; per-vehicle history is bounded (~30 min of ~10s samples).
  for (const s of hist.samples) {
    const d = Math.abs(s.ts - ts);
    if (d < bestDiff) {
      bestDiff = d;
      best = s;
    }
  }
  if (bestDiff > toleranceSec) return undefined;
  return best;
}

function gapMetersAt(
  history: HistoryStore,
  proj: RouteProjection,
  dirSign: 1 | -1,
  trailingId: string,
  leadingId: string,
  ts: number,
): number | null {
  const ta = sampleNear(
    history.byVehicle.get(trailingId),
    ts,
    TREND_MATCH_TOLERANCE_SEC,
  );
  const tb = sampleNear(
    history.byVehicle.get(leadingId),
    ts,
    TREND_MATCH_TOLERANCE_SEC,
  );
  if (!ta || !tb) return null;
  const da = proj.project(ta.lat, ta.lon);
  const db = proj.project(tb.lat, tb.lon);
  if (da == null || db == null) return null;
  const gap = (db - da) * dirSign;
  if (gap <= 0) return null; // ordering swapped at that instant; can't compare meaningfully
  return gap;
}

function computeTrend(
  history: HistoryStore,
  proj: RouteProjection,
  dirSign: 1 | -1,
  trailingId: string,
  leadingId: string,
  nowSec: number,
): { trend: Trend | null; prevGapM: number | null } {
  const recent = gapMetersAt(
    history,
    proj,
    dirSign,
    trailingId,
    leadingId,
    nowSec - TREND_WINDOW_SEC,
  );
  const older = gapMetersAt(
    history,
    proj,
    dirSign,
    trailingId,
    leadingId,
    nowSec - TREND_LOOKBACK_SEC,
  );
  if (recent == null || older == null) return { trend: null, prevGapM: null };
  const delta = recent - older;
  if (Math.abs(delta) < TREND_SIGNIFICANT_M) {
    return { trend: "steady", prevGapM: recent };
  }
  return { trend: delta < 0 ? "closing" : "opening", prevGapM: recent };
}

function pairKeyFor(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

function severityRank(s: Severity): number {
  return s === "severe" ? 0 : 1;
}

function minGap(inc: Incident): number {
  return inc.kind === "convoy" ? inc.minGapSec : inc.gapSec;
}

function formatGap(sec: number): string {
  const total = Math.max(0, Math.round(sec));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatClock(sec: number): string {
  const d = new Date(sec * 1000);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

function formatRange(start: number, end: number): string {
  const dur = Math.max(0, end - start);
  return `${formatClock(start)} → ${formatClock(end)} (${formatGap(dur)})`;
}

function trendGlyph(t: Trend): string {
  if (t === "closing") return "↘";
  if (t === "opening") return "↗";
  return "→";
}

function shortVehicleId(id: string): string {
  // Match the short numeric tail used elsewhere ("1_4001" → "4001").
  const m = id.match(/(\d{3,})\s*$/);
  return m ? m[1] : id.slice(-4);
}
